import { Suspense, useMemo } from 'react';
import { Navigate, useOutletContext, useRoutes, type RouteObject } from 'react-router-dom';

import type { LayoutRouteContext } from '../features/layout/Layout';
import type { NavItem } from '../features/layout/Navbar/Navbar';
import { findRegisteredRoute, ROUTES } from './paths';
import AppLoader from '../components/AppLoader';

const normalizePath = (path: string) => path.replace(/^\/+|\/+$/g, '');

function DynamicRoutes() {
    const { menuItems, isLoading, isError } = useOutletContext<LayoutRouteContext>();
    const routes = useMemo(() => {
        const flattenMenus = (items: NavItem[]): NavItem[] => items.flatMap(item => [item, ...(item.childMenus ? flattenMenus(item.childMenus) : [])]);
        const activeMenus = flattenMenus(menuItems).filter(menu => menu.isActive === true);
        const routeMap = new Map<string, RouteObject>();
        const addRoute = (apiPath: string) => {
            const normalizedPath = normalizePath(apiPath);
            const matchedRoute = findRegisteredRoute(normalizedPath);
            if (!matchedRoute) {
                return;
            }

            const [registeredPath, Component] = matchedRoute;
            const routePath = registeredPath.includes(':') ? registeredPath : normalizedPath;
            const key = routePath.toLowerCase();

            if (!routeMap.has(key)) {
                routeMap.set(key, {
                    path: routePath,
                    element: (
                        <Suspense fallback={<AppLoader />}>
                            <Component />
                        </Suspense>
                    ),
                });
            }
        };

        activeMenus.forEach(menu => addRoute(menu.navMenuUrl));

        return [
            ...routeMap.values(),
            {
                path: '*',
                element: isLoading ? (<AppLoader />) : isError ? (
                    <div role="alert">Unable to load the data due to a connection issue. Please check your internet connection and try again.</div>
                ) : (<Navigate to={ROUTES.home} replace />),
            },
        ];
    }, [isError, isLoading, menuItems]);

    return useRoutes(routes);
}
export default DynamicRoutes;